/**
 * Based on https://github.com/tmmdata/chartist-plugin-tooltip/blob/master/src/scripts/chartist-plugin-tooltip.js
 *
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
/* global Chartist */

import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {easings, LineChart, LineChartData, LineChartOptions} from 'chartist';
import {tooltipPlugin} from './tooltip-plugin';

export interface ChartFormatOptions {
  formatValueAs: 'currency' | 'number';
  currencyCode?: string;
  locale?: string;
}

export interface ChartEntry {
  label: string;
  value: number;
  formatOptions: ChartFormatOptions;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extra?: any;
}

@Component({
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChartComponent implements OnInit, OnChanges, OnDestroy {
  @Input() entries: Array<ChartEntry>[] = [];
  @Input() options?: LineChartOptions = {};
  @Input() labelStep: number | undefined;
  @ViewChild('chartDiv', {static: true}) private chartDivRef: ElementRef<HTMLDivElement>;
  private chart: LineChart;

  ngOnInit() {
    this.chart = new LineChart(this.chartDivRef.nativeElement, this.entriesToLineChartData(this.entries ?? []), {
      low: 0,
      showArea: false,
      showLine: true,
      showPoint: true,
      fullWidth: true,
      axisX: {
        showLabel: false,
        showGrid: false,
        offset: 1,
      },
      axisY: {
        showLabel: false,
        offset: 1,
      },
      plugins: [tooltipPlugin()],
      ...this.options,
    });

    this.chart.on('draw', data => {
      // console.log(data);
      if (data.type === 'line' || data.type === 'area') {
        data.element.animate({
          d: {
            begin: 2000 * data.index,
            dur: 2000,
            from: data.path.clone().scale(1, 0).translate(0, data.chartRect.height()).stringify(),
            to: data.path.clone().stringify(),
            easing: easings.easeOutQuint,
          },
        });
      }
    });

    // Create the gradient definition on created event (always after chart re-render)
    this.chart.on('created', ctx => {
      const defs = ctx.svg.elem('defs');
      defs
        .elem('linearGradient', {
          id: 'gradient',
          x1: 0,
          y1: 1,
          x2: 0,
          y2: 0,
        })
        .elem('stop', {
          offset: 0,
          'stop-color': 'var(--color-primary-400)',
          'stop-opacity': 0.3,
        })
        .parent()
        ?.elem('stop', {
          offset: 1,
          'stop-color': 'var(--color-primary-500)',
        });
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('entries' in changes && this.chart) {
      this.chart.update(this.entriesToLineChartData(this.entries ?? []), this.options, true);
    }
  }

  ngOnDestroy() {
    this.chart?.detach();
  }

  private entriesToLineChartData(entries: Array<ChartEntry>[]): LineChartData {
    // console.log(entries);
    let itemLength = 0;
    let resLabels;
    const resSeries = entries.map(e => {
      // console.log(e);
      const labels = e.map(({label}, index) => {
        return this.labelStep ? (!((this.labelStep + index) % this.labelStep) ? label : '') : label;
      });
      if (labels.length > itemLength) {
        itemLength = labels.length;
        resLabels = labels;
      }
      const series = e.map(({label, value, formatOptions, extra}) => ({meta: {label, formatOptions, extra}, value}));
      return series;
    });

    return {labels: resLabels, series: resSeries};

    // const labels = entries.map(({label}) => label);
    // const series = [entries.map(({label, value, formatOptions}) => ({meta: {label, formatOptions}, value}))];
    // console.log(entries, series);
    // console.log({labels, series: [...series, ...series.reverse()]});
    // return {labels, series: [...series, ...series.reverse()]};
  }
}
